import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMyInterests, getMyBookings, getInviteCount } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MapPin, Calendar, Euro, Clock, CheckCircle2, XCircle, Hourglass, Lock, Unlock, MessageCircle, FileText } from 'lucide-react';
import { cn, formatDate, formatTime, getUrgencyBadgeClass } from '../lib/utils';
import { readVenueName } from '../constants/profileContract';
import { DateWithRelative } from '../components/DateWithRelative';
import { toast } from 'sonner';
import { PageSkeleton, Bone, BoneCard } from '../components/PageSkeleton';

export default function MyInterestsPage() {
  const [interests, setInterests] = useState({ pending: [], accepted: [], rejected: [] });
  const [bookings, setBookings] = useState({ proposed: [], confirmed: [], completed: [], canceled: [], needsAction: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('action');
  const [pendingInviteCount, setPendingInviteCount] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [interestsRes, bookingsRes, inviteCountRes] = await Promise.all([
        getMyInterests(),
        getMyBookings(),
        getInviteCount(),
      ]);
      setInterests(interestsRes.data);
      setBookings(bookingsRes.data);
      setPendingInviteCount(inviteCountRes.data.count || 0);
    } catch (error) {
      toast.error('Failed to load gigs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle2 className="h-4 w-4 text-green-400" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-400" />;
      default:
        return <Hourglass className="h-4 w-4 text-yellow-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Accepted</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/50">Rejected</Badge>;
      default:
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">Pending</Badge>;
    }
  };

  // Booking Card for proposed/confirmed bookings
  const BookingCard = ({ booking }) => {
    const snapshot = booking.snapshot || {};
    const isConfirmed = booking.status === 'BASICS_CONFIRMED';
    const userConfirmed = !!booking.talentConfirmedAt;
    const needsAction = booking.needsAction;
    
    return (
      <Link to={`/bookings/${booking.id}`}>
        <Card className={cn(
          "bg-card border-border hover:border-primary/50 transition-colors cursor-pointer",
          needsAction && "border-yellow-500/50 bg-yellow-500/5"
        )}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                {isConfirmed ? (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                    <Unlock className="h-3 w-3 mr-1" />
                    Confirmed
                  </Badge>
                ) : needsAction ? (
                  <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
                    <FileText className="h-3 w-3 mr-1" />
                    Needs Confirmation
                  </Badge>
                ) : (
                  <Badge className="bg-secondary/50 text-muted-foreground border-border">
                    <Lock className="h-3 w-3 mr-1" />
                    Waiting for Venue
                  </Badge>
                )}
              </div>
              <span className="text-lg font-bold text-primary">€{snapshot.fee}</span>
            </div>

            <h3 className="font-bold text-foreground mb-2">{snapshot.role}</h3>

            <div className="space-y-1 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin className="h-3 w-3" />
                <span>{snapshot.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-3 w-3" />
                <span><DateWithRelative date={snapshot.date} /></span>
                <Clock className="h-3 w-3 ml-2" />
                <span>{formatTime(snapshot.callTime)}</span>
              </div>
            </div>

            {isConfirmed && (
              <div className="mt-3 pt-3 border-t border-border">
                <Button
                  size="sm"
                  className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white"
                  onClick={(e) => { e.stopPropagation(); const link = booking.venueWhatsappLink || booking.talentWhatsappLink; if (link) window.open(link, '_blank', 'noopener,noreferrer'); }}
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Open WhatsApp
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </Link>
    );
  };

  const RequestCard = ({ request }) => (
    <Link to={`/requests/${request.reference || request.id}`} key={request.id}>
      <Card className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={getUrgencyBadgeClass(request.urgency)}>
                {request.urgency}
              </Badge>
              {getStatusBadge(request.interestStatus)}
            </div>
            {request.budgetAmount && (
              <span className="text-lg font-bold text-primary">€{request.budgetAmount}</span>
            )}
          </div>

          <h3 className="font-bold text-foreground mb-2 line-clamp-2">{request.title}</h3>

          <div className="space-y-1 text-sm text-muted-foreground">
            {(request.venue || request.venueName) && (
              <div className="flex items-center gap-2">
                <MapPin className="h-3 w-3" />
                <span>{readVenueName(request)} • {request.venue?.city || request.city}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Calendar className="h-3 w-3" />
              <span><DateWithRelative date={request.eventDate} /></span>
              {request.startTime && (
                <>
                  <Clock className="h-3 w-3 ml-2" />
                  <span>{formatTime(request.startTime)}</span>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  const EmptyState = ({ status }) => {
    const messages = {
      action: {
        emoji: '✅',
        title: 'Nothing needs your attention',
        description: 'When a venue proposes a booking, you\'ll see it here to confirm.'
      },
      pending: {
        emoji: '⏳',
        title: 'No pending interests',
        description: 'When you show interest in a gig, it will appear here until the venue responds.'
      },
      confirmed: {
        emoji: '🎉',
        title: 'No confirmed gigs yet',
        description: 'When both you and a venue confirm a booking, the gig will show up here.'
      },
      rejected: {
        emoji: '📭',
        title: 'No rejected interests',
        description: "Gigs where you weren't selected will appear here."
      }
    };
    
    const msg = messages[status] || messages.pending;
    
    return (
      <div className="text-center py-12 px-4">
        <div className="text-4xl mb-4">{msg.emoji}</div>
        <h3 className="text-lg font-medium text-foreground mb-2">{msg.title}</h3>
        <p className="text-muted-foreground text-sm max-w-xs mx-auto">{msg.description}</p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="content-container">
        <PageSkeleton label="Loading your gigs">
          <Bone w={96} h={22} tone="card" />
          <Bone w={220} h={14} className="mt-3" />
          <Bone h={36} r="md" className="mt-6" tone="card" />
          <div className="space-y-3 mt-6">
            {[1, 2, 3].map((i) => (
              <BoneCard key={i} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <Bone w="62%" h={18} />
                  <Bone w={72} h={22} r="pill" />
                </div>
                <Bone w="48%" h={14} className="mt-3" />
                <Bone w="36%" h={14} className="mt-2" />
              </BoneCard>
            ))}
          </div>
        </PageSkeleton>
      </div>
    );
  }

  // Items needing action (bookings to confirm)
  const actionItems = bookings.proposed.filter(b => b.needsAction);
  const totalCount = interests.pending.length + bookings.confirmed.length + interests.rejected.length;

  return (
    <div className="content-container">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight text-white">
          My Gigs
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Track your gig interests and bookings
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-secondary/50 p-1 mb-6">
          <TabsTrigger 
            value="action" 
            className={cn(
              "relative",
              "data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-400"
            )}
            data-testid="tab-action"
          >
            <FileText className="h-4 w-4 mr-1.5" />
            Action
            {actionItems.length > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 bg-yellow-500 text-black text-xs font-bold rounded-full flex items-center justify-center">
                {actionItems.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger 
            value="pending" 
            className="data-[state=active]:bg-secondary data-[state=active]:text-foreground"
            data-testid="tab-pending"
          >
            <Hourglass className="h-4 w-4 mr-1.5" />
            Pending ({interests.pending.length})
          </TabsTrigger>
          <TabsTrigger 
            value="confirmed"
            className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400"
            data-testid="tab-confirmed"
          >
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            Confirmed ({bookings.confirmed.length})
          </TabsTrigger>
          <TabsTrigger 
            value="rejected"
            className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400"
            data-testid="tab-rejected"
          >
            <XCircle className="h-4 w-4 mr-1.5" />
            Rejected ({interests.rejected.length})
          </TabsTrigger>
        </TabsList>

        {/* Action Required Tab - Bookings needing confirmation */}
        <TabsContent value="action" className="space-y-4">
          {/* Pending Invites Banner */}
          {pendingInviteCount > 0 && (
            <Link to="/invites/pending">
              <div className="flex items-center justify-between p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/40 hover:border-yellow-500/70 transition-colors cursor-pointer">
                <div className="flex items-center gap-2">
                  <span className="h-5 w-5 bg-yellow-500 text-black text-xs font-bold rounded-full inline-flex items-center justify-center shrink-0">
                    {pendingInviteCount}
                  </span>
                  <span className="text-sm text-yellow-400 font-medium">
                    Pending confirmation{pendingInviteCount > 1 ? 's' : ''} — respond before they expire
                  </span>
                </div>
                <span className="text-xs text-yellow-400 underline shrink-0">View all</span>
              </div>
            </Link>
          )}
          {actionItems.length === 0 ? (
            <EmptyState status="action" />
          ) : (
            actionItems.map((booking) => (
              <BookingCard key={booking.id} booking={booking} />
            ))
          )}
        </TabsContent>

        {/* Pending Interests Tab */}
        <TabsContent value="pending" className="space-y-4">
          {interests.pending.length === 0 ? (
            <EmptyState status="pending" />
          ) : (
            interests.pending.map((request) => (
              <RequestCard key={request.id} request={request} />
            ))
          )}
        </TabsContent>

        {/* Confirmed Bookings Tab */}
        <TabsContent value="confirmed" className="space-y-4">
          {bookings.confirmed.length === 0 ? (
            <EmptyState status="confirmed" />
          ) : (
            bookings.confirmed.map((booking) => (
              <BookingCard key={booking.id} booking={booking} />
            ))
          )}
        </TabsContent>

        {/* Rejected Tab */}
        <TabsContent value="rejected" className="space-y-4">
          {interests.rejected.length === 0 ? (
            <EmptyState status="rejected" />
          ) : (
            interests.rejected.map((request) => (
              <RequestCard key={request.id} request={request} />
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
